import { useEffect, useState, useMemo } from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// material
import {
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TablePagination,
  Link,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Box,
  CircularProgress,
  Avatar,
  Paper,
  Button
} from '@mui/material';

// graphql
import { useQuery, useMutation } from '@apollo/client';
import { UserGQLFunction } from '../graphql/USER';
import secureLocalStorage from 'react-secure-storage';

// components
import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import BasicBreadcrumb from '../components/BasicBreadcrumb';
import EmptyList from '../sections/people/EmptyList';

// info
import { MESSAGE_HISTORY } from '../info/MESSAGE_HISTORY';
import { fDateTime } from '../utils/formatTime';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import Linkify from 'react-linkify';

function MessageBubble({ msg, userId }) {
  const isOwn = msg.from && msg.from.includes(userId);
  const token = secureLocalStorage.getItem('token') || '';
  const baseUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:4000' : '';
  
  const match = msg.content && typeof msg.content === 'string' ? msg.content.match(/\[.*?: (.*?)\]/) : null;
  const filename = match && match[1] !== 'unknown' ? encodeURIComponent(match[1]) : '';
  const mediaUrl = `${baseUrl}/api/msg/attachment/${msg.msgId}${filename ? `/${filename}` : ''}?token=${token}`;

  // 61: SYSTEM_NOTIF, 62: GROUP_NOTIF, 63: FRIEND_NOTIF, 72: DECRYPT_STATUS
  const isNotification = msg.msgType === 61 || msg.msgType === 62 || msg.msgType === 63 || msg.msgType === 72;
  
  if (isNotification) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 2, width: '100%' }}>
        <Typography 
          variant="caption" 
          sx={{ 
            color: 'text.secondary', 
            bgcolor: 'action.hover', 
            px: 2, 
            py: 0.5, 
            borderRadius: 2,
            fontSize: '0.75rem'
          }}
        >
          {msg.content}
        </Typography>
      </Box>
    );
  }

  let contentBlock = null;
  const isMedia = [21, 22, 23, 24].includes(msg.msgType);

  switch (msg.msgType) {
    case 21: // Image
      contentBlock = (
        <Zoom>
          <Box component="img" src={mediaUrl} sx={{ maxWidth: '100%', maxHeight: 300, borderRadius: 1 }} alt="Attachment" />
        </Zoom>
      );
      break;
    case 22: // Audio
      contentBlock = (
        <audio controls src={mediaUrl} style={{ maxWidth: '100%' }} />
      );
      break;
    case 23: // Video
      contentBlock = (
        <Zoom>
          <video controls src={mediaUrl} style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 2 }} />
        </Zoom>
      );
      break;
    case 24: // File
      contentBlock = (
        <Button variant={isOwn ? 'outlined' : 'contained'} color={isOwn ? 'inherit' : 'primary'} href={mediaUrl} target="_blank" download>
          Download File
        </Button>
      );
      break;
    default:
      contentBlock = (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
          <Linkify properties={{ target: '_blank', rel: 'noopener noreferrer' }}>
            {msg.content || `[Encrypted Type: ${msg.msgType}]`}
          </Linkify>
        </Typography>
      );
  }

  return (
    <Stack direction="row" spacing={2} justifyContent={isOwn ? 'flex-end' : 'flex-start'} sx={{ mb: 3 }}>
      {!isOwn && (
        <Avatar sx={{ bgcolor: 'secondary.main', width: 40, height: 40, fontSize: '1rem', fontWeight: 'bold' }}>
          {msg.from ? msg.from.replace('e2ee:', '').charAt(0).toUpperCase() : '?'}
        </Avatar>
      )}
      
      <Stack alignItems={isOwn ? 'flex-end' : 'flex-start'} sx={{ maxWidth: { xs: '90%', sm: '75%', md: '60%' } }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, px: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={isOwn ? 'normal' : 'bold'}>
            {msg.from.replace('e2ee:', '')}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            {fDateTime(Number(msg.timeSend))}
          </Typography>
        </Stack>
        
        <Paper
          elevation={0}
          sx={{
            p: isMedia ? 0 : 2,
            bgcolor: isMedia ? 'transparent' : (isOwn ? 'primary.main' : 'background.neutral'),
            color: isOwn ? 'primary.contrastText' : 'text.primary',
            borderRadius: 2.5,
            borderTopLeftRadius: isOwn ? 20 : 4,
            borderTopRightRadius: isOwn ? 4 : 20,
            wordBreak: 'break-word',
            boxShadow: isMedia ? 'none' : (isOwn ? '0 8px 16px rgba(0, 171, 85, 0.24)' : '0 4px 12px rgba(0,0,0,0.05)'),
            transition: 'all 0.2s ease-in-out',
            overflow: 'hidden',
            '&:hover': {
              boxShadow: isMedia ? 'none' : (isOwn ? '0 8px 20px rgba(0, 171, 85, 0.3)' : '0 6px 16px rgba(0,0,0,0.08)')
            }
          }}
        >
          {contentBlock}
        </Paper>
        {msg.decryptStatuses && msg.decryptStatuses.length > 0 && (
          <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, px: 1 }}>
            {`Decrypted${msg.decryptStatuses.length > 1 ? ` (${msg.decryptStatuses.length})` : ''}`}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

function MessageListContent({ data, userId }) {
  const processedData = useMemo(() => {
    const mainMsgs = [];
    const statusMsgs = data.filter(m => m.msgType === 72);
    
    data.forEach(msg => {
      if (msg.msgType === 72) return;
      
      const statuses = statusMsgs.filter(s => {
        try {
          const payload = JSON.parse(s.content);
          return payload.msgId === msg.msgId;
        } catch(e) {
          return false;
        }
      });
      
      mainMsgs.push({
        ...msg,
        decryptStatuses: statuses
      });
    });
    return mainMsgs;
  }, [data]);

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
      {processedData.map((msg) => (
        <MessageBubble key={msg.id} msg={msg} userId={userId} />
      ))}
    </Box>
  );
}

export default function MessageHistory() {
  const { t } = useTranslation();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const userId = queryParams.get('userId') || queryParams.get('id');
  const id = queryParams.get('id');
  const from = queryParams.get('from');

  console.log('MessageHistory location.search:', search);
  console.log('MessageHistory userId extracted:', userId);

  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  
  const [searchStr, setSearchStr] = useState('');
  const [domain, setDomain] = useState('');
  const [sessionInitialized, setSessionInitialized] = useState(false);
  const [initializing, setInitializing] = useState(false);

  const [initSession] = useMutation(UserGQLFunction.INIT_MESSAGE_HISTORY_SESSION);

  // Initialize the decrypted cache session when the component mounts
  useEffect(() => {
    if (userId && !sessionInitialized && !initializing) {
      setInitializing(true);
      initSession({ variables: { userId } })
        .then(() => {
          setSessionInitialized(true);
          setInitializing(false);
        })
        .catch((err) => {
          console.error('Failed to initialize session:', err);
          setSessionInitialized(true); // Proceed anyway to show empty or existing cache
          setInitializing(false);
        });
    }
  }, [userId, initSession, sessionInitialized, initializing]);

  // Fetch available domains (chat sessions)
  const { data: convData } = useQuery(UserGQLFunction.GET_MSG_BACKUP_CONVERSATIONS, {
    variables: { userId },
    skip: !userId
  });

  const domains = convData?.msgBackupConversations || [];

  const { loading, data } = useQuery(UserGQLFunction.GET_MSG_BACKUP_HISTORY, {
    variables: { userId, domain: domain || null, searchStr: searchStr || null, page, rowsPerPage },
    skip: !userId || !sessionInitialized,
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    if (!loading && data) {
      setTotal(data.msgBackupHistory.len);
    }
  }, [loading, data]);

  const handleSearchChange = (event) => {
    setSearchStr(event.target.value);
    setPage(0);
  };

  const handleDomainChange = (event) => {
    setDomain(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const parentPath = from === 'register' ? '/register' : '/user';
  const parentTitle = from === 'register' ? t('nav.enroll') : t('nav.users');
  const infoPath = from === 'register' ? `/register/info/${id}` : `/user/info/${id}`;

  const bread = [
    <Link component={RouterLink} to={parentPath} underline="hover" key="1" color="inherit">
      {parentTitle}
    </Link>,
    id && (
      <Link component={RouterLink} to={infoPath} underline="hover" key="2" color="inherit">
        {t('people.info_title', { identity: parentTitle })}
      </Link>
    ),
    <Typography key="3" color="text.primary">
      {t('history.title')}
    </Typography>
  ].filter(Boolean);

  if (!userId) {
    return (
      <Page title={t('history.title')}>
        <Container>
          <Typography variant="h4">No User ID provided</Typography>
        </Container>
      </Page>
    );
  }

  return (
    <Page title={t('history.title')}>
      <Container maxWidth={false}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Stack direction="column">
            <Typography variant="h4" gutterBottom>
              {t('history.title')} {userId && `(${userId})`}
            </Typography>
            <BasicBreadcrumb bread={bread} />
          </Stack>
        </Stack>

        <Card>
          <Box sx={{ p: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel id="domain-select-label">{t('history.domain_filter', 'Chat Session')}</InputLabel>
                <Select
                  labelId="domain-select-label"
                  value={domain}
                  label={t('history.domain_filter', 'Chat Session')}
                  onChange={handleDomainChange}
                >
                  <MenuItem value="">
                    <em>{t('common.all', 'All')}</em>
                  </MenuItem>
                  {domains.map((d) => (
                    <MenuItem key={d} value={d}>
                      {d}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label={t('common.search', 'Search')}
                placeholder={t('history.search_placeholder', 'Search message content...')}
                value={searchStr}
                onChange={handleSearchChange}
              />
            </Stack>
          </Box>
          <Scrollbar>
            <Box sx={{ minHeight: 400, bgcolor: 'background.default', borderTop: '1px solid', borderColor: 'divider', borderBottom: '1px solid' }}>
              {initializing || loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                  <Stack direction="column" alignItems="center" spacing={3}>
                    <CircularProgress size={48} thickness={4} color="primary" />
                    <Typography variant="body1" color="text.secondary" fontWeight="medium">
                      {initializing ? t('history.initializing', 'Initializing secure decryption cache, please wait...') : t('common.loading')}
                    </Typography>
                  </Stack>
                </Box>
              ) : data?.msgBackupHistory?.messages?.length > 0 ? (
                <MessageListContent data={data.msgBackupHistory.messages} userId={userId} />
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                  <EmptyList />
                </Box>
              )}
            </Box>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[15, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={t('common.rows_per_page')}
          />
        </Card>
      </Container>
    </Page>
  );
}
